import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "historical-chart" }
const _hoisted_2 = {
  key: 0,
  class: "chart-loading"
}
const _hoisted_3 = {
  key: 0,
  class: "chart-empty-overlay"
}
const _hoisted_4 = {
  key: 1,
  class: "chart-info"
}
const _hoisted_5 = { class: "info-item" }
const _hoisted_6 = { class: "info-value" }
const _hoisted_7 = { class: "info-item" }
const _hoisted_8 = { class: "info-value" }
const _hoisted_9 = { class: "info-item" }
const _hoisted_10 = { class: "info-value" }

import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
  shallowRef,
  markRaw,
} from "vue";

import "chartjs-adapter-luxon";
import { Chart } from "chart.js/auto";
import zoomPlugin from "chartjs-plugin-zoom";


export default /*@__PURE__*/_defineComponent({
  __name: 'HistoricalChart',
  props: {
    metric: {},
    range: {},
    data: {},
    loading: { type: Boolean },
    vin: {},
    timeRange: {}
  },
  setup(__props: any) {

Chart.register(zoomPlugin);

const props = __props;

const chartCanvas = ref<HTMLCanvasElement | null>(null);
const chartInstance = shallowRef<Chart | null>(null);

// Hint overlay (scroll senza Alt)
const showZoomHint = ref(false);
let hintTimeout: any = null;

function handleWheel(e: WheelEvent) {
  if (!e.altKey) {
    showZoomHint.value = true;
    if (hintTimeout) clearTimeout(hintTimeout);
    hintTimeout = setTimeout(() => (showZoomHint.value = false), 1800);
  } else {
    showZoomHint.value = false;
  }
}

function getMetricFieldName(metric: string): string {
  const map: Record<string, string> = {
    speed: "vehicle_speed",
    rpm: "engine_rpm",
    temperature: "engine_temperature",
    fuel: "fuel_level",
    load: "engine_load",
    battery: "tracker_battery",
  };
  return map[metric] || "vehicle_speed";
}

function getMetricLabel(metric: string) {
  switch (metric) {
    case "speed":
      return "Velocità (km/h)";
    case "rpm":
      return "RPM Motore";
    case "temperature":
      return "Temperatura (°C)";
    case "fuel":
      return "Carburante (%)";
    case "load":
      return "Carico Motore (%)";
    case "battery":
      return "Tensione Batteria (V)";
    default:
      return metric;
  }
}

function getRangeLabel(range: string) {
  switch (range) {
    case "1h":
      return "Ultima ora";
    case "24h":
      return "Ultime 24 ore";
    case "7d":
      return "Ultimi 7 giorni";
    case "30d":
      return "Ultimi 30 giorni";
    case "custom":
      return "Personalizzato";
    default:
      return range;
  }
}

/**
 * Serie {x,y} in ms (timeseries) e PLAIN (no Proxy Vue)
 */
const processedSeries = computed(() => {
  if (!props.data?.length) return [];
  const field = getMetricFieldName(props.metric);

  // console.log("props.data", props.data);
  const series = props.data.map((item: any) => {
    const ts =
      typeof item.timestamp === "string"
        ? parseInt(item.timestamp, 10)
        : Number(item.timestamp);

    const raw = item?.[field];

    // Per speed: null/undefined => 0 (fermo)
    const y =
      (raw === null || raw === undefined)
        ? 0
        : Number(raw ?? 0);
    // const y = (raw === null || raw === undefined) ? null : Number(raw);

    return { x: ts * 1000, y };
  });

  series.sort((a, b) => a.x - b.x);
  return series;
});

function toPlainSeries() {
  return processedSeries.value.map((p) => ({
    x: Number(p.x),
    y: (p.y === null ? null : Number(p.y)),
  }));
}

function destroyChart() {
  if (chartInstance.value) {
    chartInstance.value.destroy();
    chartInstance.value = null;
  }
}

async function recreateChart() {
  await nextTick();
  if (!chartCanvas.value) return;
  if (!processedSeries.value.length) {
    destroyChart();
    return;
  }

  // ricrea sempre: più stabile su cambi range/metric
  destroyChart();

  const dataSeries = toPlainSeries();
  // console.log("[Chart] timeRange props:", props.timeRange);
  // console.log("[Chart] first/last data:", dataSeries[0]?.x, dataSeries[dataSeries.length-1]?.x);
  // console.log("[Chart] last API point:", dataSeries[dataSeries.length-1]?.x, new Date(dataSeries[dataSeries.length-1]?.x).toISOString());

  const tr = props.timeRange;
  const xMin = tr?.start != null ? tr.start * 1000 : undefined;
  const xMax = tr?.end != null ? tr.end * 1000 : undefined;

  let enriched = [...dataSeries];

  // 1) se vogliamo riempire a 0 prima del primo dato reale
  if (xMin != null && enriched.length) {
    const firstX = Number(enriched[0].x);

    // se il primo dato arriva DOPO xMin, genera zeri tra xMin e firstX
    if (firstX > xMin) {
      // scegli uno step coerente col range (esempi sensati)
      const step =
        props.range === "1h"  ? 60 * 1000 :       // 1 min
        props.range === "24h" ? 5 * 60 * 1000 :   // 5 min
        props.range === "7d"  ? 60 * 60 * 1000 :  // 1h
        props.range === "30d" ? 6 * 60 * 60 * 1000 : // 6h
        60 * 60 * 1000;

      for (let t = xMin; t < firstX; t += step) {
        enriched.push({ x: t, y: 0 });
      }
    }
  }

  // aggiungi sentinelle SOLO se hai un timeRange valido
  if (xMin != null && !enriched.some(p => Number(p.x) === Number(xMin))) {
    enriched.push({ x: xMin, y: null });
  }
  if (xMax != null && !enriched.some(p => Number(p.x) === Number(xMax))) {
    enriched.push({ x: xMax, y: null });
  }

  // ordina dopo insert
  enriched.sort((a, b) => Number(a.x) - Number(b.x));

  const options: any = markRaw({
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 0 },
    parsing: false,
    spanGaps: false,
    normalized: true,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        callbacks: {
          // label: (ctx: any) => `${getMetricLabel(props.metric)}: ${ctx.parsed.y}`,
          label: (ctx: any) => {
            const y = ctx.parsed?.y;
            if (y === null || y === undefined) return `${getMetricLabel(props.metric)}: N/D`;
            return `${getMetricLabel(props.metric)}: ${y}`;
          },
        },
      },
      zoom: {
        zoom: {
          wheel: { enabled: true, modifierKey: "alt", speed: 0.1 },
          pinch: { enabled: true },
          mode: "x",
        },
        pan: {
          enabled: true,
          mode: "x",
          modifierKey: "shift",
        },
        // limits: {
        //   x: { min: "original", max: "original" },
        //   y: { min: "original", max: "original" },
        // },
        limits: {
          x: {
            min: xMin ?? "original",
            max: xMax ?? "original",
          },
          y: { min: "original", max: "original" },
        },
      },
    },
    scales: {
      x: {
        type: "timeseries",
        min: xMin,
        max: xMax,
        bounds: "ticks",
        time: {
          unit: (props.range === "1h" ? "minute" : props.range === "24h" ? "hour" : "day"),
          tooltipFormat: "dd/LL/yyyy HH:mm",
        },
        // time: { tooltipFormat: "dd/LL/yyyy HH:mm" },
        ticks: { autoSkip: true, includeBounds: true, maxTicksLimit: 10, color: "#9fb0c2" },
        grid: { display: false },
        title: { display: true, text: "Tempo", color: "#c9d3de" },
      },
      y: {
        beginAtZero: props.metric !== "temperature",
        ticks: { color: "#9fb0c2" },
        grid: { color: "rgba(255,255,255,0.08)" },
        title: { display: true, text: getMetricLabel(props.metric), color: "#c9d3de" },
      },
    },
  });

  const chart = new Chart(chartCanvas.value, {
    type: "line",
    data: {
      datasets: [
        {
          label: getMetricLabel(props.metric),
          data: enriched,
          borderColor: "#4caf50",
          backgroundColor: "rgba(76, 175, 80, 0.12)",
          borderWidth: 2,
          fill: true,
          tension: 0.35,
          pointRadius: 0,
          pointHitRadius: 10,
        },
      ],
    },
    options,
  });

  // console.log("[Chart scale] x.min/x.max (ms):", chart.scales.x.min, chart.scales.x.max);
  // console.log("[Chart scale] x.min/x.max (iso):",
  //   new Date(chart.scales.x.min).toISOString(),
  //   new Date(chart.scales.x.max).toISOString()
  // );
  // console.log("[Chart wanted] xMin/xMax (iso):",
  //   xMin ? new Date(xMin).toISOString() : null,
  //   xMax ? new Date(xMax).toISOString() : null
  // );

  chartInstance.value = markRaw(chart);
}

// Mount
onMounted(async () => {
  if (!props.loading) {
    await recreateChart();
  }
});

onBeforeUnmount(() => {
  if (hintTimeout) clearTimeout(hintTimeout);
  destroyChart();
});

/**
 * Trigger principali:
 * - quando finisce il loading (tipico cambio range: loading true -> false)
 * - quando cambiano data/metric/range/vin
 *
 * NB: ascolto anche vin perché spesso cambia insieme al range e conviene ripulire tutto.
 */
watch(
  () => props.loading,
  async (isLoading) => {
    if (!isLoading) {
      await recreateChart();
    } else {
      // opzionale: mentre carica, evita interazioni strane
      destroyChart();
    }
  },
  { flush: "post" }
);

watch(
  () => [props.metric, props.range, props.vin],
  async () => {
    // range/metric cambiano spesso prima che arrivino i dati:
    // se stai caricando, ci penserà il watcher di loading a ricreare.
    if (!props.loading) {
      await recreateChart();
    }
  },
  { flush: "post" }
);

watch(
  () => props.data,
  async () => {
    // se il parent rimpiazza l’array, questo scatta sicuro
    if (!props.loading) {
      await recreateChart();
    }
  },
  { deep: false, flush: "post" }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.loading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[0] || (_cache[0] = [
          _createElementVNode("div", { class: "spinner" }, null, -1),
          _createTextVNode(" Caricamento grafico... ", -1)
        ]))]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: "chart-container",
      onWheel: handleWheel
    }, [
      _createElementVNode("canvas", {
        ref_key: "chartCanvas",
        ref: chartCanvas
      }, null, 512),
      (!__props.loading && !processedSeries.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, " Nessun dato disponibile per il periodo selezionato "))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        class: _normalizeClass(["zoom-hint-overlay", { show: showZoomHint.value }])
      }, [...(_cache[1] || (_cache[1] = [
        _createElementVNode("div", { class: "zoom-hint-content" }, [
          _createTextVNode(" Usa "),
          _createElementVNode("kbd", null, "Alt"),
          _createTextVNode(" + rotella per zoomare "),
          _createElementVNode("span", { class: "hint-sub" }, [
            _createTextVNode("Pan: "),
            _createElementVNode("kbd", null, "Shift"),
            _createTextVNode(" + drag")
          ])
        ], -1)
      ]))], 2)
    ], 32),
    (processedSeries.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _cache[2] || (_cache[2] = _createElementVNode("span", { class: "info-label" }, "Metrica:", -1)),
            _createElementVNode("span", _hoisted_6, _toDisplayString(getMetricLabel(__props.metric)), 1)
          ]),
          _createElementVNode("div", _hoisted_7, [
            _cache[3] || (_cache[3] = _createElementVNode("span", { class: "info-label" }, "Periodo:", -1)),
            _createElementVNode("span", _hoisted_8, _toDisplayString(getRangeLabel(__props.range)), 1)
          ]),
          _createElementVNode("div", _hoisted_9, [
            _cache[4] || (_cache[4] = _createElementVNode("span", { class: "info-label" }, "Dati disponibili:", -1)),
            _createElementVNode("span", _hoisted_10, _toDisplayString(processedSeries.value.length) + " punti", 1)
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})